/**
 * Shared authentication utility functions
 * Extracted to avoid circular dependencies between auth.ts and AuthContext.tsx
 */

import { clearAccessToken } from '../auth/accessToken';
import { postAuthMessage } from '../auth/authChannel';

/**
 * The pre-17.2 web-storage token keys. Story 17.2 moved the access token into
 * memory and the refresh token into an HttpOnly cookie; these keys are never
 * written. The legacy `refresh_token` is only READ for the one-time migration
 * (the first refresh in `client.ts`, a pre-17.2 tab's logout in `auth.ts`, and
 * `hasSessionHint` below) and then REMOVED. Follow-up
 * T-20261005-2 deletes those reads.
 */
export const LEGACY_TOKEN_KEYS = ['access_token', 'refresh_token'] as const;

/** Remove the pre-17.2 token keys from localStorage. Never throws. */
export function removeLegacyTokenKeys(): void {
  try {
    for (const key of LEGACY_TOKEN_KEYS) localStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing stored there to remove.
  }
}

/**
 * Is there any sign this browser holds a session worth a refresh POST? The
 * cookie is HttpOnly and invisible, so the hint is the identity JSON login
 * writes (or a pre-17.2 legacy refresh token). An anonymous visitor has
 * neither: `AuthContext`'s mount skips the refresh, and `client.ts` refuses one
 * for a tab that also holds no access token, so a 401 on a signed-out tab (a
 * wrong password on /login) is not made to wait out a doomed refresh.
 */
export function hasSessionHint(): boolean {
  try {
    return (
      localStorage.getItem('user') !== null || localStorage.getItem('refresh_token') !== null
    );
  } catch {
    return false;
  }
}

/**
 * Clear all authentication state: the in-memory access token, any legacy
 * token keys, and the `user` / `tenant` identity JSON.
 */
export function clearAuthState(): void {
  clearAccessToken();
  removeLegacyTokenKeys();
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Check if a JWT access token is expired
 * @param token - JWT access token
 * @returns true if token is expired or invalid, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  // Safety check: if token is undefined or not a string, treat as expired
  if (!token || typeof token !== 'string') {
    return true;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiration = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expiration;
  } catch {
    return true; // If we can't decode, treat as expired
  }
}

/**
 * Tell every other tab this session logged out (Story 17.2: over the
 * `amatis-auth` BroadcastChannel; the old `logout-event` storage key is gone).
 */
export function broadcastLogoutEvent(): void {
  postAuthMessage({ type: 'logout' });
}
